import { useEffect, useMemo, useRef } from 'react';
import { useRegistryGiftBasketOrdersByIdsQuery, useRegistryGiftBasketItemsByIdsQuery, useGetRegistryGuideCompletionPercentageQuery } from '@graphql/generated';
import type { UseGiftBasketContentControllerArgs } from './GiftBasketContent.types';
import { processRegistryItems, processRegistryOrders } from './GiftBasketContent.utils';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useCatalogContext } from '../../../Catalog';
import { useEnableChecklist } from '../../hooks';

export const useGiftBasketContentController = (args: UseGiftBasketContentControllerArgs) => {
  const { currentTab, registryItemIds, registryOrderIds, totalRegistryItemsCount, totalTrackedGiftsCount } = args;
  const { formatCurrency } = useCurrencyFormatter();
  const productContainerRef = useRef<HTMLDivElement>(null);

  const { eventHandle } = useCatalogContext();
  const enableChecklistInGiftBasketWidget = useEnableChecklist();

  const { data: registryItemsData, loading: registryItemsLoading } = useRegistryGiftBasketItemsByIdsQuery({
    batchMode: 'fast',
    skip: currentTab !== 'giftList',
    variables: { ids: registryItemIds },
  });

  const { data: registryOrdersData, loading: registryOrdersLoading } = useRegistryGiftBasketOrdersByIdsQuery({
    batchMode: 'fast',
    skip: currentTab !== 'giftTracker' || enableChecklistInGiftBasketWidget,
    variables: { ids: registryOrderIds },
  });

  const { data: getRegistryAdvisorData, loading: registryAdvisorDataLoading } = useGetRegistryGuideCompletionPercentageQuery({
    variables: { handle: eventHandle || '' },
    batchMode: 'fast',
    skip: currentTab !== 'giftList' || !eventHandle,
  });

  const processedRegistryOrders = useMemo(() => {
    return processRegistryOrders(registryOrdersData, formatCurrency, totalTrackedGiftsCount);
  }, [formatCurrency, registryOrdersData, totalTrackedGiftsCount]);

  const processedRegistryItems = useMemo(() => {
    return processRegistryItems(registryItemsData, formatCurrency, totalRegistryItemsCount);
  }, [formatCurrency, registryItemsData, totalRegistryItemsCount]);

  const registryAdvisorCompletionPercentage = useMemo(() => {
    const counts = getRegistryAdvisorData?.eventByName?.recommendation.registryAdvisor;
    if (counts) {
      return Math.round((counts.currentGiftCount / counts.targetGiftCount) * 100);
    }
    return 0;
  }, [getRegistryAdvisorData]);

  useEffect(() => {
    if (productContainerRef.current) {
      // Always scroll to top when changing tabs
      productContainerRef.current.scrollTop = 0;
    }
  }, [currentTab]);

  const dataPoints = currentTab === 'giftList' ? processedRegistryItems : processedRegistryOrders;

  return {
    productContainerRef,
    loading: currentTab === 'giftList' ? registryItemsLoading : registryOrdersLoading,
    ...dataPoints,
    hasData: !!dataPoints.productRowDataProps.length,
    registryAdvisorCompletionPercentage,
    registryAdvisorDataLoading,
    currentAdvisedGiftCount: getRegistryAdvisorData?.eventByName?.recommendation.registryAdvisor?.currentGiftCount || 0,
    targetAdvisedGiftCount: getRegistryAdvisorData?.eventByName?.recommendation.registryAdvisor?.targetGiftCount || 0,
  };
};
